/**
 * Add-on products (CALLOUT) - merge add-ons into SAME cart request + reliable deselection sync
 * + Tooltip (question + answer) via AddOnTip
 *
 * FIX (Search jump / weird behavior):
 * - Do NOT run outside-click cleanup in capture phase (prevents interfering with <summary>/<details> search)
 * - Ignore clicks/focus inside header/search UI
 * - Keep only ONE global listener set
 *
 * EXTRA FIX (discount code input on PDP / cart drawer):
 * - Do NOT interfere with cart drawer / cart notification / discount code UI
 * - Do NOT use global focusin cleanup (can break coupon input focus)
 *
 * EXTRA FIX (PDP main price discount UI):
 * - show original main price striked
 * - show discounted main price
 *
 * EXTRA FIX:
 * - add-ons must NOT change main product price
 *
 * EXTRA FIX (scroll jump):
 * - closed tooltips are fully non-focusable (hidden + aria-hidden + inert)
 * - guard against external accessibility scripts forcing focus into closed tips
 */

(function () {
  const SELECTORS = {
    container: '[data-js-addon-container]',
    item: '[data-js-addon-item]',
    checkbox: 'input[type="checkbox"][data-addon-variant-id]',
    infoBtn: '[data-js-addon-info-button]',
    modal: '[data-js-addon-modal]',
    modalClose: '[data-js-addon-modal-close]',
    overlay: '[data-js-addon-overlay]',
    tip: '[data-js-addon-tip]',
    tipClose: '[data-js-addon-tip-close]',
  };

  const STORAGE_KEY = 'lf_pending_addons_merge_callout_v1';
  const CART_CHANGED_EVENT = 'lf:cart-changed';

  // Written by discount-apply.js once it has resolved a discount code.
  const RESOLVED_DISCOUNT_KEY = 'scDiscountData';

  function isContainerVisible(container) {
    if (!container || !container.isConnected) return false;

    const wrapper = container.closest(
      '.LF04PdpAddons, .LFPdpAddonsImage'
    );

    const target = wrapper || container;
    const style = window.getComputedStyle(target);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      target.getClientRects().length > 0
    );
  }

  function isSaftkurCalloutContainer(container) {
    return !!(
      container &&
      container.closest &&
      container.closest('.SaftkurCallout')
    );
  }

  function getAddonContainers() {
    return Array.from(
      document.querySelectorAll(
        SELECTORS.container
      )
    ).filter((container) => {
      return !isSaftkurCalloutContainer(container);
    });
  }

  function isFeatOwnedDiscountContainer(container) {
    return !!(
      container &&
      container.matches &&
      container.matches(
        '.FeatCollectionImage__Addons[data-feat-discount-owner="true"]'
      )
    );
  }

  function getActiveContainer() {
    const containers =
      getAddonContainers();

    return (
      containers.find(isContainerVisible) ||
      containers[0] ||
      null
    );
  }

  function markContainerDirty(container) {
    if (!container) return;

    container.dataset.addonSelectionDirty = 'true';
  }

  function clearContainerDirty(container) {
    if (!container) return;

    delete container.dataset.addonSelectionDirty;
  }

  function isContainerDirty(container) {
    return (
      container &&
      container.dataset.addonSelectionDirty === 'true'
    );
  }

  const CART_MUT_RE =
    /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  const CART_ADD_RE =
    /\/cart\/add(\.js)?(\?|$)/i;

  const HEADER_SEARCH_SELECTOR = [
    'sticky-header',
    '.header-wrapper',
    'header',
    'details-modal',
    '.header__search',
    '.search-modal',
    '.modal__toggle',
    '.modal-overlay',
    'predictive-search',
    '[data-predictive-search]',
    '#Search-In-Modal',
    'input[type="search"]',
    '.search__input',
    '.field__input',
    '.LIV-547-searchbar',
    '.LIV-547-searchbar-mobile',
  ].join(',');

  const PROTECTED_UI_SELECTOR = [
    HEADER_SEARCH_SELECTOR,
    'cart-drawer',
    '#CartDrawer',
    '.cart-drawer',
    '.drawer',
    '.drawer__inner',
    '.drawer__header',
    '.drawer__content',
    '.drawer__footer',
    'cart-notification',
    '.cart-notification',
    '.cart-notification-wrapper',
    '.cart-count-bubble',
    'cart-items',
    '.cart-items',
    '.totals',
    '.cart__footer',
    '.cart__blocks',
    '.cart__ctas',
    '.discounts',
    '[data-discount]',
    '[data-discount-form]',
    '[name="discount"]',
    '[name="discount_code"]',
    'input[name*="discount"]',
    'input[id*="discount"]',
    'input[autocomplete="off"]',
    'details',
    'summary',
    'input',
    'textarea',
    'select',
    'button',
    'label',
    '[contenteditable="true"]',
  ].join(',');

  function isInHeaderOrSearch(element) {
    try {
      return !!(
        element &&
        element.closest &&
        element.closest(HEADER_SEARCH_SELECTOR)
      );
    } catch (error) {
      return false;
    }
  }

  function isInProtectedUi(element) {
    try {
      return !!(
        element &&
        element.closest &&
        element.closest(PROTECTED_UI_SELECTOR)
      );
    } catch (error) {
      return false;
    }
  }

  function toInt(value, fallback = 0) {
    const number = parseInt(
      String(value ?? '').replace(/[^\d-]/g, ''),
      10
    );

    return Number.isFinite(number)
      ? number
      : fallback;
  }

  function formatMoney(cents, moneyFormat) {
    try {
      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function'
      ) {
        return window.Shopify.formatMoney(
          cents,
          moneyFormat
        );
      }
    } catch (error) {}

    const amount = (cents / 100)
      .toFixed(2)
      .replace('.', ',');

    return amount + ' €';
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error('Failed to load cart');
    }

    return response.json();
  }

  function getMoneyFormat(container) {
    return (
      container.getAttribute('data-money-format') ||
      '${{amount}}'
    );
  }

  function getBasePrice(container) {
    return toInt(
      container.getAttribute('data-base-price'),
      0
    );
  }

  function setBasePrice(container, cents) {
    container.setAttribute(
      'data-base-price',
      String(toInt(cents, 0))
    );
  }

  function getPriceTarget(container) {
    const targetId = container.getAttribute(
      'data-price-target-id'
    );

    if (targetId) {
      return document.getElementById(targetId);
    }

    const sectionId = container.getAttribute(
      'data-section-id'
    );

    if (sectionId) {
      return document.getElementById(
        `price-${sectionId}`
      );
    }

    return null;
  }

  function findThemePriceNodes(priceRoot) {
    if (!priceRoot) {
      return {
        regular: null,
        saleCurrent: null,
        saleCompare: null,
        fallback: null,
      };
    }

    const regular =
      priceRoot.querySelector(
        '.price__regular .price-item--regular'
      ) ||
      priceRoot.querySelector(
        '.price__regular .price-item'
      ) ||
      priceRoot.querySelector(
        '.price-item--regular'
      );

    const saleCurrent =
      priceRoot.querySelector(
        '.price__sale .price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price__sale .price-item--sale'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale'
      );

    const saleCompare =
      priceRoot.querySelector(
        '.price__sale .price-item--regular'
      ) ||
      null;

    const fallback =
      saleCurrent ||
      regular ||
      priceRoot.querySelector('.price-item') ||
      priceRoot.querySelector('.price') ||
      priceRoot;

    return {
      regular,
      saleCurrent,
      saleCompare,
      fallback,
    };
  }

  function getStablePriceContainer(priceRoot) {
    if (!priceRoot) return null;

    return (
      priceRoot.querySelector(':scope > .price') ||
      priceRoot.querySelector('.price') ||
      priceRoot.querySelector('.price__container') ||
      priceRoot
    );
  }

  function getOrCreateMainPriceUi(priceRoot) {
    if (!priceRoot) return {};

    let host = priceRoot.querySelector(
      ':scope > .AddOnProducts__MainPriceRow'
    );

    if (!host) {
      const stableContainer =
        getStablePriceContainer(priceRoot);

      host = document.createElement('span');
      host.className =
        'AddOnProducts__MainPriceRow';

      host.setAttribute(
        'data-js-main-price-ui',
        'true'
      );

      host.hidden = true;

      if (
        stableContainer &&
        stableContainer !== priceRoot &&
        stableContainer.parentNode === priceRoot
      ) {
        priceRoot.insertBefore(
          host,
          stableContainer
        );
      } else {
        priceRoot.prepend(host);
      }
    }

    let original = host.querySelector(
      '.AddOnProducts__MainPriceOriginal'
    );

    if (!original) {
      original = document.createElement('span');
      original.className =
        'AddOnProducts__MainPriceOriginal';

      original.hidden = true;
      host.appendChild(original);
    }

    let discounted = host.querySelector(
      '.AddOnProducts__MainPriceDiscounted'
    );

    if (!discounted) {
      discounted = document.createElement('span');
      discounted.className =
        'AddOnProducts__MainPriceDiscounted';

      discounted.hidden = true;
      host.appendChild(discounted);
    }

    return {
      host,
      original,
      discounted,
    };
  }

  function updateSelectedUI(container) {
    container
      .querySelectorAll(SELECTORS.item)
      .forEach((row) => {
        const checkbox = row.querySelector(
          SELECTORS.checkbox
        );

        row.classList.toggle(
          'is-selected',
          !!(checkbox && checkbox.checked)
        );
      });
  }

  function getSelectedAddonsTotal(
    container,
    cart = null
  ) {
    let total = 0;

    const parentDiscount =
      getCartDiscount(
        container,
        cart
      );

    container
      .querySelectorAll(SELECTORS.checkbox)
      .forEach((checkbox) => {
        if (!checkbox.checked) return;

        const basePrice = toInt(
          checkbox.getAttribute(
            'data-addon-price'
          ),
          0
        );

        if (!basePrice) return;

        const addonLine = findCartLine(
          cart,
          checkbox.getAttribute(
            'data-addon-variant-id'
          )
        );

        const discount = addonLine
          ? (
              discountFromCartLine(
                addonLine
              ) ||
              parentDiscount
            )
          : parentDiscount;

        total +=
          discount?.active
            ? Math.max(
                0,
                Math.round(
                  basePrice *
                    discount.factor
                )
              )
            : basePrice;
      });

    return total;
  }

  function getBuyButtonPriceElements(container) {
    const formId = container.getAttribute(
      'data-product-form-id'
    );

    if (!formId) return null;

    const form = document.getElementById(formId);

    if (!form) return null;

    const buyButtons = form.closest('.BuyButtons');

    if (
      !buyButtons ||
      !buyButtons.hasAttribute(
        'data-buy-buttons-with-price'
      )
    ) {
      return null;
    }

    const priceWrapper = buyButtons.querySelector(
      '[data-js-product-submit-price]'
    );

    const priceValue = buyButtons.querySelector(
      '[data-js-product-submit-price-value]'
    );

    if (!priceWrapper || !priceValue) {
      return null;
    }

    return {
      buyButtons,
      priceWrapper,
      priceValue,
    };
  }

  function updateBuyButtonTotal(container) {
    /*
     * feat-collection-image owns its discount preview + ATC total.
     * Keep the generic add-on script responsible for selection/cart merge,
     * but never let it overwrite the section-specific sticky discount UI.
     */
    if (
      isFeatOwnedDiscountContainer(container)
    ) {
      return;
    }

    const elements =
      getBuyButtonPriceElements(container);

    if (!elements) return;

    const activeContainer =
      getActiveContainer();

    if (
      activeContainer &&
      activeContainer !== container &&
      !isContainerVisible(container)
    ) {
      return;
    }

    const cart =
      window.__lfAddonsLastKnownCartPdp ||
      null;

    const basePrice =
      getBasePrice(container);

    const baseDiscount =
      getCartDiscount(
        container,
        cart
      );

    const effectiveBasePrice =
      baseDiscount?.active
        ? Math.max(
            0,
            Math.round(
              basePrice *
                baseDiscount.factor
            )
          )
        : basePrice;

    const addonsTotal =
      getSelectedAddonsTotal(
        container,
        cart
      );

    const finalTotal = Math.max(
      0,
      effectiveBasePrice +
        addonsTotal
    );

    const moneyFormat =
      elements.buyButtons.getAttribute(
        'data-money-format'
      ) ||
      getMoneyFormat(container);

    elements.priceWrapper.setAttribute(
      'data-price',
      String(finalTotal)
    );

    elements.priceWrapper.setAttribute(
      'data-base-product-price',
      String(basePrice)
    );

    elements.priceWrapper.setAttribute(
      'data-selected-addons-total',
      String(addonsTotal)
    );

    elements.priceValue.textContent =
      formatMoney(finalTotal, moneyFormat);
  }

  function normalizeScopeId(raw) {
    const value = String(raw ?? '').trim();

    if (!value) return '';

    const parts = value.split('/');

    return (
      parts[parts.length - 1] ||
      value
    ).trim();
  }

  function readResolvedDiscount() {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem(
            RESOLVED_DISCOUNT_KEY
          )
        ) ||
        null
      );
    } catch (error) {
      return null;
    }
  }

  function getScopePriceElement(container) {
    const priceRoot = getPriceTarget(container);

    if (!priceRoot) return null;

    if (
      priceRoot.dataset?.productId ||
      priceRoot.dataset?.collections
    ) {
      return priceRoot;
    }

    return (
      priceRoot.querySelector(
        '.price[data-product-id], .price[data-collections], [data-product-id][data-collections], .price'
      ) ||
      priceRoot
    );
  }

  function resolvedDiscountAppliesTo(
    priceElement,
    resolved
  ) {
    if (!priceElement || !resolved) {
      return false;
    }

    const productId = normalizeScopeId(
      priceElement.dataset.productId
    );

    const resolvedProducts = (
      resolved.products || []
    ).map(normalizeScopeId);

    if (
      productId &&
      resolvedProducts.includes(
        productId
      )
    ) {
      return true;
    }

    const scopedCollections = new Set(
      (resolved.collections || []).map(
        normalizeScopeId
      )
    );

    return String(
      priceElement.dataset.collections || ''
    )
      .split(',')
      .map(normalizeScopeId)
      .filter(Boolean)
      .some((id) =>
        scopedCollections.has(id)
      );
  }

  function getSessionDiscount(container) {
    const resolved = readResolvedDiscount();

    if (
      !resolvedDiscountAppliesTo(
        getScopePriceElement(container),
        resolved
      )
    ) {
      return null;
    }

    const type = String(
      resolved.discount?.type || ''
    );

    const value = parseFloat(
      resolved.discount?.value
    );

    if (
      !Number.isFinite(value) ||
      value <= 0
    ) {
      return null;
    }

    if (type === 'percentage') {
      return {
        active: true,
        factor: 1 - value / 100,
        pct: window.roundDiscount(value),
      };
    }

    if (type === 'fixed') {
      const basePrice =
        getBasePrice(container);

      if (basePrice <= 0) return null;

      const discounted = Math.max(
        0,
        basePrice - Math.round(value * 100)
      );

      const factor =
        discounted / basePrice;

      return {
        active: true,
        factor,
        pct: window.roundDiscount(
          (1 - factor) * 100
        ),
      };
    }

    return null;
  }

  function findCartLine(cart, variantId) {
    const id = String(variantId || '');

    if (!id) return null;

    return (
      cart?.items || []
    ).find(
      (item) =>
        String(item.variant_id) === id
    ) || null;
  }

  function discountFromCartLine(line) {
    for (
      const allocation of
      line.line_level_discount_allocations || []
    ) {
      const application =
        allocation.discount_application;

      if (
        application &&
        application.value_type === 'percentage' &&
        parseFloat(application.value) > 0
      ) {
        const codePercentage =
          parseFloat(application.value);

        return {
          active: true,
          factor:
            1 - codePercentage / 100,
          pct: window.roundDiscount(
            codePercentage
          ),
        };
      }
    }

    const original = toInt(
      line.original_line_price,
      0
    );

    const final = toInt(
      line.final_line_price,
      -1
    );

    if (
      original > 0 &&
      final >= 0 &&
      final < original
    ) {
      const factor = final / original;

      return {
        active: true,
        factor,
        pct: window.roundDiscount(
          (1 - factor) * 100
        ),
      };
    }

    return null;
  }

  const NO_DISCOUNT = {
    active: false,
    factor: 1,
    pct: 0,
  };

  function getCartDiscount(container, cart) {
    const parentVariantId =
      container.getAttribute(
        'data-parent-variant-id'
      );

    const line = findCartLine(
      cart,
      parentVariantId
    );

    const sessionDiscount =
      getSessionDiscount(container);

    if (
      line &&
      String(
        line.properties?._addon || ''
      ) !== 'true'
    ) {
      return (
        discountFromCartLine(line) ||
        sessionDiscount ||
        NO_DISCOUNT
      );
    }

    return (
      sessionDiscount ||
      NO_DISCOUNT
    );
  }

  function updateMainPrice(container, cart) {
    const base = getBasePrice(container);
    const priceRoot =
      getPriceTarget(container);

    if (!priceRoot) return;

    const moneyFormat =
      getMoneyFormat(container);

    const ui =
      getOrCreateMainPriceUi(priceRoot);

    const theme =
      findThemePriceNodes(priceRoot);

    if (!cart) {
      if (theme.regular) {
        theme.regular.textContent =
          formatMoney(base, moneyFormat);
      }

      if (theme.saleCurrent) {
        theme.saleCurrent.textContent =
          formatMoney(base, moneyFormat);
      }

      if (theme.saleCompare) {
        theme.saleCompare.textContent =
          formatMoney(base, moneyFormat);
      }

      if (ui.original) {
        ui.original.hidden = true;
        ui.original.textContent = '';
      }

      if (ui.discounted) {
        ui.discounted.hidden = true;
        ui.discounted.textContent = '';
      }

      if (ui.host) {
        ui.host.hidden = true;
      }

      return;
    }

    const discount =
      getCartDiscount(container, cart);

    if (!discount.active) {
      if (theme.regular) {
        theme.regular.textContent =
          formatMoney(base, moneyFormat);
      }

      if (theme.saleCurrent) {
        theme.saleCurrent.textContent =
          formatMoney(base, moneyFormat);
      }

      if (theme.saleCompare) {
        theme.saleCompare.textContent =
          formatMoney(base, moneyFormat);
      }

      if (ui.original) {
        ui.original.hidden = true;
        ui.original.textContent = '';
      }

      if (ui.discounted) {
        ui.discounted.hidden = true;
        ui.discounted.textContent = '';
      }

      if (ui.host) {
        ui.host.hidden = true;
      }

      return;
    }

    const discountedBase = Math.round(
      base * discount.factor
    );

    if (theme.regular) {
      theme.regular.textContent = '';
    }

    if (theme.saleCurrent) {
      theme.saleCurrent.textContent = '';
    }

    if (theme.saleCompare) {
      theme.saleCompare.textContent = '';
    }

    if (ui.original) {
      ui.original.hidden = false;
      ui.original.textContent =
        formatMoney(base, moneyFormat);
    }

    if (ui.discounted) {
      ui.discounted.hidden = false;
      ui.discounted.textContent =
        formatMoney(
          discountedBase,
          moneyFormat
        );
    }

    if (ui.host) {
      ui.host.hidden = false;
    }
  }

  function applyDiscountToAddonRows(
    container,
    cart
  ) {
    /*
     * The feat collection component has a dedicated sticky renderer.
     * Do not let generic cart/discount resyncs clear or rewrite its rows.
     */
    if (
      isFeatOwnedDiscountContainer(container)
    ) {
      return;
    }

    const parentDiscount =
      getCartDiscount(container, cart);

    const moneyFormat =
      getMoneyFormat(container);

    container
      .querySelectorAll(SELECTORS.item)
      .forEach((row) => {
        const checkbox = row.querySelector(
          SELECTORS.checkbox
        );

        const priceWrap = row.querySelector(
          '.AddOnItem__Price'
        );

        const currentElement =
          row.querySelector(
            '.AddOnItem__CurrentPrice'
          );

        if (
          !checkbox ||
          !priceWrap ||
          !currentElement
        ) {
          return;
        }

        let discountedElement =
          row.querySelector(
            '.AddOnItem__DiscountedPrice'
          );

        if (!discountedElement) {
          discountedElement =
            document.createElement('span');

          discountedElement.className =
            'AddOnItem__DiscountedPrice';

          discountedElement.hidden = true;
          priceWrap.appendChild(
            discountedElement
          );
        }

        const addonCents = toInt(
          checkbox.getAttribute(
            'data-addon-price'
          ),
          0
        );

        const addonLine = findCartLine(
          cart,
          checkbox.getAttribute(
            'data-addon-variant-id'
          )
        );

        const discount = addonLine
          ? (
              discountFromCartLine(
                addonLine
              ) ||
              parentDiscount
            )
          : parentDiscount;

        if (
          !discount.active ||
          !addonCents
        ) {
          row.classList.remove(
            'is-cart-discount-active'
          );

          discountedElement.hidden = true;
          discountedElement.textContent = '';

          return;
        }

        const discounted = Math.round(
          addonCents * discount.factor
        );

        row.classList.add(
          'is-cart-discount-active'
        );

        discountedElement.hidden = false;

        discountedElement.textContent =
          formatMoney(
            discounted,
            moneyFormat
          );
      });
  }

  function applyCartStateToUI(
    container,
    cart,
    options = {}
  ) {
    if (!container) return;

    const forceSelectionSync =
      options.forceSelectionSync === true;

    /*
     * Do not overwrite a selection the customer has just made on the PDP.
     * The selected add-on is not in cart yet, so syncing from /cart.js here
     * would immediately deselect it.
     */
    if (
      !isContainerDirty(container) ||
      forceSelectionSync
    ) {
      const inCart = new Set(
        (cart?.items || []).map(
          (item) =>
            toInt(item.variant_id, 0)
        )
      );

      container
        .querySelectorAll(
          SELECTORS.checkbox
        )
        .forEach((checkbox) => {
          const variantId = toInt(
            checkbox.getAttribute(
              'data-addon-variant-id'
            ),
            0
          );

          checkbox.checked =
            inCart.has(variantId);
        });

      if (forceSelectionSync) {
        clearContainerDirty(container);
      }
    }

    updateSelectedUI(container);
    updateBuyButtonTotal(container);

    applyDiscountToAddonRows(
      container,
      cart
    );
  }

  function createThrottled(
    callback,
    wait
  ) {
    let timer = null;

    return function () {
      if (timer) return;

      timer = setTimeout(() => {
        timer = null;
        callback();
      }, wait);
    };
  }

  function ensureGlobalResolvedDiscountSync() {
    if (
      window.__lfAddonsResolvedDiscountSyncBoundV1
    ) {
      return;
    }

    window.__lfAddonsResolvedDiscountSyncBoundV1 =
      true;

    document.addEventListener(
      'lf:discount-resolved',
      (event) => {
        const cart =
          event.detail
            ? (
                window.__lfAddonsLastKnownCartPdp ||
                null
              )
            : null;

        const containers =
          getAddonContainers();

        containers.forEach(
          (container) => {
            applyDiscountToAddonRows(
              container,
              cart
            );
          }
        );

        const activeContainer =
          getActiveContainer();

        if (activeContainer) {
          updateBuyButtonTotal(
            activeContainer
          );
        }
      }
    );
  }

  function ensureGlobalCartResync() {
    if (
      window.__lfAddonsCartResyncBoundV1
    ) {
      return;
    }

    window.__lfAddonsCartResyncBoundV1 =
      true;

    const resync = createThrottled(
      async () => {
        try {
          const cart = await fetchCart();

          window.__lfAddonsLastKnownCartPdp =
            cart;

          const containers =
            getAddonContainers();

          containers.forEach(
            (container) => {
              applyCartStateToUI(
                container,
                cart,
                {
                  forceSelectionSync: false,
                }
              );
            }
          );

          const activeContainer =
            getActiveContainer();

          if (activeContainer) {
            updateBuyButtonTotal(
              activeContainer
            );
          }
        } catch (error) {}
      },
      200
    );

    window.__lfAddonsCartResync =
      resync;

    document.addEventListener(
      CART_CHANGED_EVENT,
      resync
    );

    document.addEventListener(
      'cart:updated',
      resync
    );

    document.addEventListener(
      'cart:refresh',
      resync
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          resync();
        }
      }
    );

    window.addEventListener(
      'focus',
      resync
    );
  }

  function openModal(container, id) {
    const overlay = container.querySelector(
      SELECTORS.overlay
    );

    const modal = container.querySelector(
      `[data-js-addon-modal="${CSS.escape(id)}"]`
    );

    if (overlay) {
      overlay.classList.add('is-open');
    }

    if (modal) {
      modal.classList.add('is-open');
    }

    document.documentElement.classList.add(
      'AddonModalOpen'
    );
  }

  function closeAllModals(container) {
    const overlay = container.querySelector(
      SELECTORS.overlay
    );

    if (overlay) {
      overlay.classList.remove('is-open');
    }

    container
      .querySelectorAll(SELECTORS.modal)
      .forEach((modal) =>
        modal.classList.remove('is-open')
      );

    if (
      document.documentElement.classList.contains(
        'AddonModalOpen'
      )
    ) {
      document.documentElement.classList.remove(
        'AddonModalOpen'
      );
    }
  }

  function setTipClosedState(tip) {
    if (!tip) return;

    tip.classList.remove(
      'is-open',
      'is-above'
    );

    tip.hidden = true;
    tip.setAttribute(
      'aria-hidden',
      'true'
    );

    tip.setAttribute('inert', '');
    tip.style.top = '';
    tip.style.left = '';

    tip.style.removeProperty(
      '--arrow-x'
    );
  }

  function setTipOpenState(tip) {
    if (!tip) return;

    tip.hidden = false;
    tip.classList.add('is-open');

    tip.setAttribute(
      'aria-hidden',
      'false'
    );

    tip.removeAttribute('inert');
  }

  function closeAllTips(container) {
    container
      .querySelectorAll(SELECTORS.tip)
      .forEach((tip) => {
        setTipClosedState(tip);
      });
  }

  function closeAllTipsEverywhere() {
    document
      .querySelectorAll(
        SELECTORS.container
      )
      .forEach((container) => {
        closeAllTips(container);
      });
  }

  function positionTip(
    buttonElement,
    tipElement
  ) {
    if (
      !buttonElement ||
      !tipElement
    ) {
      return;
    }

    const buttonRect =
      buttonElement.getBoundingClientRect();

    const wasClosed =
      tipElement.hidden ||
      !tipElement.classList.contains(
        'is-open'
      );

    if (wasClosed) {
      setTipOpenState(tipElement);
    }

    const tipRect =
      tipElement.getBoundingClientRect();

    const viewportWidth =
      window.innerWidth;

    const viewportHeight =
      window.innerHeight;

    let top =
      buttonRect.bottom + 10;

    let left =
      buttonRect.left +
      buttonRect.width / 2 -
      tipRect.width / 2;

    left = Math.max(
      12,
      Math.min(
        left,
        viewportWidth -
          tipRect.width -
          12
      )
    );

    if (
      top +
        tipRect.height +
        12 >
      viewportHeight
    ) {
      top =
        buttonRect.top -
        tipRect.height -
        10;

      tipElement.classList.add(
        'is-above'
      );
    } else {
      tipElement.classList.remove(
        'is-above'
      );
    }

    top = Math.max(8, top);

    tipElement.style.top =
      `${top + window.scrollY}px`;

    tipElement.style.left =
      `${left + window.scrollX}px`;

    const arrowCenter =
      buttonRect.left +
      buttonRect.width / 2 -
      left;

    tipElement.style.setProperty(
      '--arrow-x',
      `${arrowCenter}px`
    );
  }

  function bindTipHandlers(container) {
    container.addEventListener(
      'click',
      (event) => {
        const infoButton =
          event.target.closest(
            SELECTORS.infoBtn
          );

        if (
          infoButton &&
          container.contains(infoButton)
        ) {
          event.preventDefault();
          event.stopPropagation();

          const id =
            infoButton.getAttribute(
              'data-addon-info-target'
            );

          const tip =
            container.querySelector(
              `[data-js-addon-tip="${CSS.escape(id)}"]`
            );

          if (!tip) {
            if (id) {
              openModal(container, id);
            }

            return;
          }

          const wasOpen =
            !tip.hidden &&
            tip.classList.contains(
              'is-open'
            );

          closeAllTipsEverywhere();

          if (!wasOpen) {
            setTipOpenState(tip);

            requestAnimationFrame(() =>
              positionTip(
                infoButton,
                tip
              )
            );
          }

          return;
        }

        const tipClose =
          event.target.closest(
            SELECTORS.tipClose
          );

        if (
          tipClose &&
          container.contains(tipClose)
        ) {
          event.preventDefault();
          closeAllTips(container);

          return;
        }

        const modalClose =
          event.target.closest(
            SELECTORS.modalClose
          );

        if (
          modalClose &&
          container.contains(modalClose)
        ) {
          event.preventDefault();
          closeAllModals(container);

          return;
        }

        const overlay =
          event.target.closest(
            SELECTORS.overlay
          );

        if (
          overlay &&
          container.contains(overlay)
        ) {
          event.preventDefault();
          closeAllModals(container);
        }
      }
    );

    container.addEventListener(
      'mouseleave',
      () => {
        closeAllTips(container);
      }
    );
  }

  function ensureGlobalOutsideHandlers() {
    if (
      window.__lfAddonsCalloutGlobalOutsideBoundV4
    ) {
      return;
    }

    window.__lfAddonsCalloutGlobalOutsideBoundV4 =
      true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (
          isInHeaderOrSearch(target)
        ) {
          return;
        }

        if (
          isInProtectedUi(target)
        ) {
          return;
        }

        const insideAddon =
          target &&
          target.closest
            ? target.closest(
                SELECTORS.container
              )
            : null;

        if (insideAddon) return;

        closeAllTipsEverywhere();
      }
    );

    document.addEventListener(
      'focusin',
      (event) => {
        const tip =
          event.target &&
          event.target.closest
            ? event.target.closest(
                SELECTORS.tip
              )
            : null;

        if (!tip) return;

        if (
          tip.hidden ||
          tip.getAttribute(
            'aria-hidden'
          ) === 'true' ||
          tip.hasAttribute('inert')
        ) {
          try {
            if (
              document.activeElement &&
              typeof document.activeElement
                .blur === 'function'
            ) {
              document.activeElement.blur();
            }
          } catch (error) {}
        }
      },
      true
    );

    window.addEventListener(
      'resize',
      () => closeAllTipsEverywhere()
    );

    window.addEventListener(
      'scroll',
      () => closeAllTipsEverywhere(),
      {
        passive: true,
      }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          closeAllTipsEverywhere();
        }
      }
    );
  }

  function writePending(payload) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(payload)
      );
    } catch (error) {}
  }

  function readPending() {
    try {
      const raw =
        sessionStorage.getItem(
          STORAGE_KEY
        );

      if (!raw) return null;

      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(
        STORAGE_KEY
      );
    } catch (error) {}
  }

  function getSelectedAddonVariantIds(
    container
  ) {
    const ids = [];

    container
      .querySelectorAll(
        SELECTORS.checkbox
      )
      .forEach((checkbox) => {
        if (!checkbox.checked) return;

        const variantId = toInt(
          checkbox.getAttribute(
            'data-addon-variant-id'
          ),
          0
        );

        if (variantId) {
          ids.push(variantId);
        }
      });

    return ids;
  }

  function bindCheckboxUI(container) {
    container.addEventListener(
      'pointerdown',
      (event) => {
        const actionButton =
          event.target &&
          event.target.closest
            ? event.target.closest(
                '.AddOnItemV2__Button'
              )
            : null;

        if (
          !actionButton ||
          !container.contains(
            actionButton
          )
        ) {
          return;
        }

        event.preventDefault();
      }
    );

    container.addEventListener(
      'click',
      (event) => {
        const actionButton =
          event.target &&
          event.target.closest
            ? event.target.closest(
                '.AddOnItemV2__Button'
              )
            : null;

        if (
          !actionButton ||
          !container.contains(
            actionButton
          )
        ) {
          return;
        }

        const row =
          actionButton.closest(
            SELECTORS.item
          );

        const checkbox = row
          ? row.querySelector(
              SELECTORS.checkbox
            )
          : null;

        if (
          !checkbox ||
          checkbox.disabled
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        checkbox.checked =
          !checkbox.checked;

        checkbox.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        requestAnimationFrame(() => {
          window.scrollTo(
            scrollX,
            scrollY
          );
        });
      }
    );

    container.addEventListener(
      'change',
      (event) => {
        const checkbox =
          event.target &&
          event.target.matches &&
          event.target.matches(
            SELECTORS.checkbox
          )
            ? event.target
            : null;

        if (!checkbox) return;

        markContainerDirty(container);
        updateSelectedUI(container);
        updateBuyButtonTotal(container);

        if (
          window.__lfAddonsLastKnownCartPdp
        ) {
          applyDiscountToAddonRows(
            container,
            window.__lfAddonsLastKnownCartPdp
          );
        }
      }
    );
  }

  function bindFormCapture(container) {
    const formId =
      container.getAttribute(
        'data-product-form-id'
      );

    if (!formId) return;

    const form =
      document.getElementById(formId);

    if (!form) return;

    if (
      form.__lfAddonsCaptureBoundCallout
    ) {
      return;
    }

    form.__lfAddonsCaptureBoundCallout =
      true;

    form.addEventListener(
      'submit',
      () => {
        /*
         * Original and Variation may both exist in the DOM.
         * Always use the currently visible add-on component.
         */
        const activeContainer =
          getActiveContainer();

        if (!activeContainer) {
          clearPending();
          return;
        }

        const addonIds =
          getSelectedAddonVariantIds(
            activeContainer
          );

        if (!addonIds.length) {
          clearPending();
          return;
        }

        writePending({
          createdAt: Date.now(),
          addonVariantIds: addonIds,
          parentHandle:
            activeContainer.getAttribute(
              'data-parent-handle'
            ) || '',
          parentVariantId:
            activeContainer.getAttribute(
              'data-parent-variant-id'
            ) || '',
        });
      },
      true
    );
  }

  function ensureGlobalVariantListener() {
    if (
      window.__lfAddonsVariantListenerBoundV1
    ) {
      return;
    }

    window.__lfAddonsVariantListenerBoundV1 =
      true;

    document.addEventListener(
      'variant:change',
      (event) => {
        const variant =
          event.detail &&
          event.detail.variant;

        if (
          !variant ||
          typeof variant.price ===
            'undefined'
        ) {
          return;
        }

        const activeContainer =
          getActiveContainer();

        if (!activeContainer) {
          return;
        }

        setBasePrice(
          activeContainer,
          variant.price
        );

        if (
          window.__lfAddonsLastKnownCartPdp
        ) {
          applyDiscountToAddonRows(
            activeContainer,
            window.__lfAddonsLastKnownCartPdp
          );
        }

        updateBuyButtonTotal(
          activeContainer
        );
      }
    );
  }

  function buildItemsFromFormData(
    formData
  ) {
    const mainId = toInt(
      formData.get('id'),
      0
    );

    const quantity =
      toInt(
        formData.get('quantity'),
        1
      ) || 1;

    const properties = {};

    for (
      const [key, value] of
      formData.entries()
    ) {
      const match = String(key).match(
        /^properties\[(.+)\]$/
      );

      if (match) {
        properties[match[1]] =
          value;
      }
    }

    const item = {
      id: mainId,
      quantity,
    };

    if (
      Object.keys(properties).length
    ) {
      item.properties = properties;
    }

    return mainId
      ? [item]
      : [];
  }

  function buildItemsFromUrlEncoded(
    bodyString
  ) {
    const params =
      new URLSearchParams(
        bodyString
      );

    const formData =
      new FormData();

    for (
      const [key, value] of
      params.entries()
    ) {
      formData.append(key, value);
    }

    return buildItemsFromFormData(
      formData
    );
  }

  function tryParseJSONBody(body) {
    try {
      return JSON.parse(body);
    } catch (error) {
      return null;
    }
  }

  function dispatchCartChanged() {
    try {
      document.dispatchEvent(
        new CustomEvent(
          CART_CHANGED_EVENT
        )
      );
    } catch (error) {}
  }

  function ensureNetworkPatches() {
    if (
      window.__lfAddonsNetworkPatchedCalloutV1
    ) {
      return;
    }

    window.__lfAddonsNetworkPatchedCalloutV1 =
      true;

    const originalFetch =
      window.fetch.bind(window);

    window.fetch = async function (
      input,
      init
    ) {
      const url =
        typeof input === 'string'
          ? input
          : input && input.url
            ? input.url
            : '';

      const isCartMutation =
        CART_MUT_RE.test(url);

      const isCartAdd =
        CART_ADD_RE.test(url);

      if (
        isCartAdd &&
        !window.__lfAddonsMergingNowCallout &&
        init &&
        (
          init.method || 'POST'
        ).toUpperCase() === 'POST'
      ) {
        const pending =
          readPending();

        const isFresh =
          pending &&
          Date.now() -
            (pending.createdAt || 0) <=
            60 * 1000;

        const addonIds =
          isFresh &&
          Array.isArray(
            pending.addonVariantIds
          )
            ? pending.addonVariantIds
            : [];

        if (addonIds.length) {
          try {
            window.__lfAddonsMergingNowCallout =
              true;

            let mainItems = [];

            const headers =
              new Headers(
                init.headers || {}
              );

            const body = init.body;

            if (
              body instanceof FormData
            ) {
              mainItems =
                buildItemsFromFormData(
                  body
                );
            } else if (
              typeof body === 'string'
            ) {
              const json =
                tryParseJSONBody(body);

              if (
                json &&
                Array.isArray(json.items)
              ) {
                mainItems =
                  json.items;
              } else {
                mainItems =
                  buildItemsFromUrlEncoded(
                    body
                  );
              }
            } else if (
              body &&
              typeof body === 'object'
            ) {
              if (
                Array.isArray(body.items)
              ) {
                mainItems =
                  body.items;
              }
            }

            const items =
              Array.isArray(mainItems)
                ? [...mainItems]
                : [];

            const parentHandle =
              pending.parentHandle || '';

            const parentVariantId =
              pending.parentVariantId || '';

            addonIds.forEach(
              (variantId) => {
                items.push({
                  id: variantId,
                  quantity: 1,
                  properties: {
                    _addon: 'true',
                    _addon_for:
                      parentHandle,
                    _addon_for_variant:
                      parentVariantId,
                  },
                });
              }
            );

            clearPending();

            headers.set(
              'Content-Type',
              'application/json'
            );

            const newInit = {
              ...init,
              headers,
              body: JSON.stringify({
                items,
              }),
            };

            const response =
              await originalFetch(
                input,
                newInit
              );

            if (
              response &&
              response.ok
            ) {
              document
                .querySelectorAll(
                  SELECTORS.container
                )
                .forEach(
                  clearContainerDirty
                );

              dispatchCartChanged();
            }

            return response;
          } catch (error) {
            clearPending();

            window.__lfAddonsMergingNowCallout =
              false;

            return originalFetch(
              input,
              init
            );
          } finally {
            window.__lfAddonsMergingNowCallout =
              false;
          }
        }
      }

      const response =
        await originalFetch(
          input,
          init
        );

      if (
        isCartMutation &&
        response &&
        response.ok
      ) {
        /*
         * Do NOT clear the local add-on selection here.
         *
         * A generic cart mutation may come from another theme component,
         * the cart drawer or an A/B test while the customer is still
         * selecting add-ons on the PDP.
         *
         * At this point the selected add-on is not necessarily in /cart.js.
         * Clearing the dirty state here would allow the following cart resync
         * to overwrite the locally selected checkbox and deselect it.
         *
         * Dirty state is cleared only when the merged parent + add-ons
         * request above succeeds.
         */
        dispatchCartChanged();
      }

      return response;
    };

    const OriginalXHR =
      window.XMLHttpRequest;

    function PatchedXHR() {
      const xhr =
        new OriginalXHR();

      let requestUrl = '';

      const originalOpen =
        xhr.open;

      xhr.open = function (
        method,
        url
      ) {
        requestUrl =
          String(url || '');

        return originalOpen.apply(
          xhr,
          arguments
        );
      };

      xhr.addEventListener(
        'load',
        function () {
          try {
            if (
              xhr.status >= 200 &&
              xhr.status < 300 &&
              CART_MUT_RE.test(
                requestUrl
              )
            ) {
              /*
               * Do not clear addonSelectionDirty here.
               *
               * Generic XHR cart mutations must not make /cart.js
               * authoritative for a still-pending PDP add-on selection.
               */
              dispatchCartChanged();
            }
          } catch (error) {}
        }
      );

      return xhr;
    }

    window.XMLHttpRequest =
      PatchedXHR;
  }

  function initContainer(container) {
    bindTipHandlers(container);
    bindCheckboxUI(container);
    bindFormCapture(container);

    updateSelectedUI(container);
    closeAllTips(container);
  }

  async function initialSync() {
    try {
      const cart = await fetchCart();

      window.__lfAddonsLastKnownCartPdp =
        cart;

      const containers =
        getAddonContainers();

      containers.forEach(
        (container) => {
          applyCartStateToUI(
            container,
            cart,
            {
              forceSelectionSync: false,
            }
          );
        }
      );

      const activeContainer =
        getActiveContainer();

      if (activeContainer) {
        updateBuyButtonTotal(
          activeContainer
        );
      }
    } catch (error) {
      getAddonContainers().forEach(
        (container) => {
          applyDiscountToAddonRows(
            container,
            null
          );
        }
      );
    }
  }

  function ensureAddonDiscountStyles() {
    if (
      document.getElementById(
        'lf-addon-discount-preview-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'lf-addon-discount-preview-styles';

    style.textContent = `
      .AddOnItem.is-cart-discount-active .AddOnItem__CurrentPrice {
        text-decoration: line-through;
        opacity: 0.65;
      }

      .AddOnItem.is-cart-discount-active .AddOnItem__DiscountedPrice {
        display: inline !important;
        margin-left: 6px;
        color: #125c46;
        font-weight: 700;
        text-decoration: none;
      }

      .AddOnItem.is-cart-discount-active .AddOnItem__DiscountedPrice[hidden] {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
  }

  function boot() {
    console.warn(
      '[LF ADDON PRODUCTS FEAT-OWNED DISCOUNT LOADED] 2026-09-07-v2'
    );

    ensureAddonDiscountStyles();
    ensureNetworkPatches();
    ensureGlobalOutsideHandlers();
    ensureGlobalVariantListener();
    ensureGlobalResolvedDiscountSync();
    ensureGlobalCartResync();

    const containers =
      getAddonContainers();

    if (!containers.length) return;

    containers.forEach(
      (container) => {
        initContainer(container);
      }
    );

    initialSync();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }
})();